import type { DashboardSummary } from '@brechoflow/contracts';
import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useDashboardFilters, useDashboardSummary } from './dashboard-api';
import { DashboardFiltersBar } from './filters';
import { formatInt } from './kpi';
import { InlineEmpty, InlineError } from './parts';
import { DashboardPanels } from './blocks';

function Cell({ id, label, detail, children }: { id: string; label: string; detail?: ReactNode; children: ReactNode }) {
  return (
    // subgrid: rótulo, número e detalhe de todos os cartões da mesma linha ficam alinhados
    <div data-testid={id} className="grid min-w-0 row-span-3 grid-rows-subgrid border-r border-b px-4 py-3.5">
      <dt className="text-[0.8125rem] leading-tight text-muted-foreground">{label}</dt>
      <dd className="contents">
        <div className="mt-1.5 font-display text-[1.625rem] leading-none font-semibold tracking-tight tabular" data-testid={`${id}-value`}>
          {children}
        </div>
        {detail && <div className="mt-1.5 text-xs leading-snug text-muted-foreground">{detail}</div>}
      </dd>
    </div>
  );
}

/**
 * Faixa de indicadores. Só renderiza os blocos que o servidor devolveu (null = sem permissão).
 * FEATURE_DASHBOARD_KPIS: produtos, estoque, pedidos e financeiro entram aqui nos módulos do brechó.
 */
export function KpiStrip({ data }: { data: DashboardSummary }) {
  const { customers: c, finance: f, orders: o, stock: st, consignment: cs, sales, returns: r } = data;
  return (
    <div className="overflow-hidden rounded-panel border bg-card" data-testid="kpi-strip">
      <dl className="-mr-px -mb-px m-0 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        {o && (
          <>
            <Cell id="kpi-revenue-day" label="Faturamento hoje" detail={`Semana ${formatCurrency(o.revenueWeekCents)} · Mês ${formatCurrency(o.revenueMonthCents)}`}>
              {formatCurrency(o.revenueTodayCents)}
            </Cell>
            <Cell id="kpi-revenue-period" label="Faturamento do período" detail={`${formatInt(o.ordersPaidPeriod)} ${o.ordersPaidPeriod === 1 ? 'pedido pago' : 'pedidos pagos'}`}>
              {formatCurrency(o.revenuePeriodCents)}
            </Cell>
            <Cell id="kpi-ticket" label="Ticket médio" detail="Por pedido pago">
              {formatCurrency(o.ticketAvgCents)}
            </Cell>
            <Cell id="kpi-orders-pending" label="Pedidos pendentes" detail={`${formatInt(o.paid)} pagos · ${formatInt(o.shipped)} enviados · ${formatInt(o.completed)} concluídos`}>
              {formatInt(o.pending)}
            </Cell>
          </>
        )}
        {st && (
          <>
            <Cell id="kpi-stock-available" label="Peças disponíveis" detail={`${formatInt(st.soldInPeriod)} vendidas no período · ${formatInt(st.soldTotal)} no total`}>
              {formatInt(st.available)}
            </Cell>
            <Cell id="kpi-stock-value" label="Estoque a preço de venda" detail={`Custo ${formatCurrency(st.costValueCents)}`}>
              {formatCurrency(st.saleValueCents)}
            </Cell>
            <Cell id="kpi-stock-stale" label="Peças paradas" detail={`Disponíveis há mais de ${st.staleDays} dias`}>
              {formatInt(st.stale)}
            </Cell>
          </>
        )}
        {sales && (
          <Cell id="kpi-margin" label="Margem realizada" detail={`Estimada no estoque: ${formatCurrency(sales.estimatedMarginCents)}`}>
            {formatCurrency(sales.realizedMarginCents)}
          </Cell>
        )}
        {cs && (
          <Cell id="kpi-consign-payable" label="Comissões a pagar" detail={`${formatInt(cs.consignorsWithBalance)} ${cs.consignorsWithBalance === 1 ? 'consignante' : 'consignantes'} com saldo`}>
            {formatCurrency(cs.commissionsPayableCents)}
          </Cell>
        )}
        {c && (
          <Cell id="kpi-customers-new" label="Clientes novos" detail={`${formatInt(c.totalActive)} ativos no total`}>
            {formatInt(c.newInPeriod)}
          </Cell>
        )}
        {f && (
          <>
            <Cell id="kpi-fin-payable" label="Contas a pagar" detail={f.payable.overdueCount > 0 ? `${formatCurrency(f.payable.overdueCents)} vencido (${formatInt(f.payable.overdueCount)})` : `${formatCurrency(f.payable.dueNext7Cents)} nos próximos 7 dias`}>
              {formatCurrency(f.payable.openCents)}
            </Cell>
            <Cell id="kpi-fin-receivable" label="Contas a receber" detail={f.receivable.overdueCount > 0 ? `${formatCurrency(f.receivable.overdueCents)} vencido (${formatInt(f.receivable.overdueCount)})` : `${formatCurrency(f.receivable.dueNext7Cents)} nos próximos 7 dias`}>
              {formatCurrency(f.receivable.openCents)}
            </Cell>
            <Cell id="kpi-fin-cashflow" label="Fluxo de caixa do mês" detail={`Projetado: ${formatCurrency(f.cashFlowMonth.projectedNetCents)}`}>
              {formatCurrency(f.cashFlowMonth.netCents)}
            </Cell>
          </>
        )}
        {r && (
          <Cell id="kpi-returns-open" label="Devoluções em aberto" detail={`${formatInt(r.awaitingInspection)} aguardando inspeção · ${formatCurrency(r.refundedLast30Cents)} reembolsado em 30 dias`}>
            {formatInt(r.open)}
          </Cell>
        )}
      </dl>
    </div>
  );
}

export function Dashboard() {
  const f = useDashboardFilters();
  const q = useDashboardSummary(f);
  const data = q.data;
  const hasBlocks = Boolean(data && (data.customers || data.finance || data.orders || data.stock || data.consignment || data.returns));
  const showSkeleton = !f.rangeError && q.isPending;

  return (
    <div data-testid="dashboard">
      <DashboardFiltersBar f={f} />
      {f.rangeError ? null : showSkeleton ? (
        <div aria-busy="true" aria-label="Carregando indicadores">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : q.isError && !data ? (
        <InlineError message="Não foi possível carregar os indicadores agora." onRetry={() => void q.refetch()} />
      ) : data ? (
        <div className={cn('transition-opacity', q.isPlaceholderData && 'opacity-60')} aria-busy={q.isPlaceholderData || undefined}>
          {q.isError && <InlineError message="Não foi possível atualizar os indicadores." onRetry={() => void q.refetch()} />}
          {hasBlocks ? (
            <>
              <KpiStrip data={data} />
              <DashboardPanels data={data} />
            </>
          ) : (
            <InlineEmpty>Seu perfil não inclui indicadores. Use o menu para acessar as áreas liberadas para você.</InlineEmpty>
          )}
        </div>
      ) : null}
    </div>
  );
}
